import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useEffect, useRef, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import {
  BidV2Status,
  type EvalBidDto,
  type EvalCellDto,
  type EvalCriterionDto,
} from "../../../../autogen/bff-api";
import { AttachmentRow } from "../../../../common/documents/AttachmentRow";
import { altKeyLabel, isExactlyAlt } from "../../../../common/platform";
import { alwaysVisibleScrollbar, revealedColumnProps } from "../../../../common/scroll";
import { TipTap, isTiptapContentEmpty } from "../../../../common/input/TipTap/TipTap";
import { formatPercent } from "../../../sourcing-events/buyer/wizard/utils/awardCriteriaStatus";
import { AwardCriterionTypeBadge } from "../../../sourcing-events/bid-readonly/AwardCriterionTypeBadge";
import { evalCriterionOwnLabel } from "./criterionLabel";
import { toReadonlyAnswer } from "./supplierAnswer";
import { ReadonlyAnswerCard } from "../../../sourcing-events/bid-readonly/ReadonlyAnswerCard";
import { AwardCriterionScoreFields } from "../bid-detail/AwardCriterionScoreFields";
import type { UseGridEditor } from "../bid-detail/useBuyerAwardCriterionScoreEditor";
import { criterionTypeLabelKey } from "../../../sourcing-events/buyer/wizard/utils/publicProcurementOptions";

const isCriterionStep = (event: KeyboardEvent<HTMLElement>) =>
  isExactlyAlt(event) && (event.key === "ArrowLeft" || event.key === "ArrowRight");

// In a field the OS owns Alt+arrow: it moves the caret a word. The justification keeps that.
const isTextEntry = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.tagName === "TEXTAREA" || target.tagName === "INPUT" || target.isContentEditable);

interface Props {
  criterion: EvalCriterionDto;
  descriptionHtml: string | null;
  bids: EvalBidDto[];
  cellFor: (bid: EvalBidDto) => EvalCellDto | undefined;
  editor: UseGridEditor;
  isReadOnly: boolean;
  position: { current: number; total: number };
  onPrevious: () => void;
  onNext: () => void;
  onClose: () => void;
}

export const FocusModeDialog = ({
  criterion,
  descriptionHtml,
  bids,
  cellFor,
  editor,
  isReadOnly,
  position,
  onPrevious,
  onNext,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const cardRow = useRef<HTMLDivElement>(null);
  const stacked = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;

  useEffect(() => {
    if (cardRow.current) cardRow.current.scrollLeft = 0;
  }, [criterion.id]);

  return (
    <Modal isOpen onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        maxW="min(96rem, 95vw)"
        maxH="90vh"
        rounded="2xl"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        onKeyDown={(event) => {
          if (isTextEntry(event.target) || !isCriterionStep(event)) return;
          // Without this, Alt+ArrowLeft is Back in the browser on Windows and Linux.
          event.preventDefault();
          if (event.key === "ArrowRight" && position.current < position.total) onNext();
          if (event.key === "ArrowLeft" && position.current > 1) onPrevious();
        }}
      >
        <ModalHeader px={{ base: "4", md: "6" }} pt="6" pb="2">
          <Flex align="center" justify="space-between" gap="4">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="smPrimary"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              {t("Focus mode")}
            </Text>
            <Button variant="outline" leftIcon={<FiX />} onClick={onClose}>
              {t("Back to the overview")}
            </Button>
          </Flex>
        </ModalHeader>

        <ModalBody display="flex" flexDirection="column" gap="4" px={{ base: "4", md: "6" }} pb="2">
          <Box borderWidth="1px" borderColor="smBorder" rounded="lg" p="4">
            <Flex align="center" gap="3" flexWrap="wrap">
              <AwardCriterionTypeBadge type={criterion.type} />
              <Text fontSize="lg" fontWeight="bold" color="smText">
                {evalCriterionOwnLabel(criterion, String(t(criterionTypeLabelKey(criterion.type))))}
              </Text>
              {criterion.parentName && criterion.weightPercent != null && (
                <Badge bg="smStatusOkBg" color="smStatusOkText" textTransform="none" rounded="md" px="2" py="0.5">
                  {t("{{weight}} within {{criterion}}", {
                    weight: formatPercent(criterion.weightPercent),
                    criterion: criterion.parentName,
                  })}
                </Badge>
              )}
              {criterion.effectiveWeightPercent != null && (
                <Badge bg="smStatusNeutralBg" color="smStatusNeutralText" textTransform="none" rounded="md" px="2" py="0.5">
                  {t("Weight {{effective}} of the total", {
                    effective: formatPercent(criterion.effectiveWeightPercent),
                  })}
                </Badge>
              )}
            </Flex>
            {descriptionHtml && !isTiptapContentEmpty(descriptionHtml) && (
              <Box mt="2" fontSize="sm" color="smMuted">
                <TipTap content={descriptionHtml} editable={false} hasBorder={false} padding="0" maxHeight="" />
              </Box>
            )}
          </Box>

          <Flex
            ref={cardRow}
            direction={stacked ? "column" : "row"}
            gap="4"
            align="stretch"
            overflowX={stacked ? "visible" : "auto"}
            pb={stacked ? "0" : "3"}
            // Chrome puts a scrollable region in the tab order and rings it; an explicit tabindex opts out.
            tabIndex={-1}
            outline="none"
            sx={alwaysVisibleScrollbar}
          >
            {bids.map((bid) => (
              <BidColumn
                key={bid.bidId}
                bid={bid}
                cell={cellFor(bid)}
                criterion={criterion}
                editor={editor}
                isReadOnly={isReadOnly}
                stacked={stacked}
              />
            ))}
          </Flex>

        </ModalBody>

        <ModalFooter px={{ base: "4", md: "6" }} py="5" borderTopWidth="1px" borderColor="smBorder">
          <Flex align="center" justify="space-between" gap="4" width="full">
            <Button
              variant="outline"
              leftIcon={<FiChevronLeft />}
              onClick={onPrevious}
              isDisabled={position.current <= 1}
              aria-keyshortcuts="Alt+ArrowLeft"
              title={`${altKeyLabel()} \u2190`}
            >
              {t("Previous requirement")}
            </Button>
            <Text fontSize="sm" color="smMuted" whiteSpace="nowrap">
              {t("Requirement {{current}} of {{total}}", position)}
            </Text>
            <Button
              colorScheme="teal"
              rightIcon={<FiChevronRight />}
              onClick={onNext}
              isDisabled={position.current >= position.total}
              aria-keyshortcuts="Alt+ArrowRight"
              title={`${altKeyLabel()} \u2192`}
            >
              {t("Next requirement")}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const BidColumn = ({
  bid,
  cell,
  criterion,
  editor,
  isReadOnly,
  stacked,
}: {
  bid: EvalBidDto;
  cell: EvalCellDto | undefined;
  criterion: EvalCriterionDto;
  editor: UseGridEditor;
  isReadOnly: boolean;
  stacked: boolean;
}) => {
  const { t } = useTranslation();
  const criterionId = criterion.id;
  const score = editor.scoreOf(bid.bidId, criterionId);
  const attachments = cell?.supplierAnswer?.attachments ?? [];
  const isComputed = cell?.isAutoComputed ?? false;
  const isRejected = bid.status === BidV2Status.Rejected;

  return (
    <Box
      data-testid="bid-column"
      role="group"
      aria-label={bid.supplierName || String(t("Unknown"))}
      tabIndex={isComputed ? 0 : undefined}
      flex={stacked ? "0 0 auto" : "1 0 24rem"}
      borderWidth="1px"
      borderColor="smBorder"
      rounded="lg"
      bg={isRejected ? "smErrorSoft" : "smBackground"}
      outline="none"
      _focusWithin={{ borderColor: "smSuccess" }}
      {...revealedColumnProps()}
    >
      <ReadonlyAnswerCard
        chrome="plain"
        title={bid.supplierName || String(t("Unknown"))}
        titleSuffix={isRejected ? <Badge colorScheme="red">{t("Avvist")}</Badge> : undefined}
        answer={toReadonlyAnswer(cell?.supplierAnswer, criterion)}
        footer={
          <Stack spacing="3" pt="4" borderTopWidth="1px" borderColor="smBorder">
            {attachments.length > 0 && (
              <Stack spacing="1">
                {attachments.map((attachment) => (
                  <AttachmentRow key={attachment.id} attachment={attachment} />
                ))}
              </Stack>
            )}
            {cell?.isAutoComputed ? (
              <ComputedScore points={cell.points ?? null} />
            ) : (
              <AwardCriterionScoreFields
                size="sm"
                points={score.points}
                comment={score.comment}
                isReadOnly={isReadOnly || isRejected}
                onPointsChange={(value) => editor.setPoints(bid.bidId, criterionId, value)}
                onCommentChange={(value) => editor.setComment(bid.bidId, criterionId, value)}
              />
            )}
          </Stack>
        }
      />
    </Box>
  );
};

const ComputedScore = ({ points }: { points: number | null }) => {
  const { t } = useTranslation();
  return (
    <Flex align="baseline" gap="2">
      <Text fontSize="lg" fontWeight="bold" fontFamily="mono" color="smChipBlueFg">
        {points != null ? points.toFixed(2) : "–"}
      </Text>
      <Text fontSize="xs" color="smMuted" textTransform="uppercase">
        {t("auto")}
      </Text>
    </Flex>
  );
};
